import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api, buildSrcSet, pickMediaUrl } from "@/lib/api";
import { ArrowLeft, Play } from "lucide-react";

const PAGE_SIZE = 24;

export default function WeddingDetail() {
  const { slug } = useParams();
  const [w, setW] = useState(null);
  const [reels, setReels] = useState([]);
  const [videos, setVideos] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    api.get(`/weddings/${slug}`)
      .then((r) => setW(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!w) return;
    // Reels associated with this wedding
    api.get("/reels").then((r) => {
      setReels((r.data || []).filter((x) => x.wedding_id === w.id));
    }).catch(() => {});
    // Videos live on the wedding row (small count, load with meta)
    setVideos(w.videos || []);
    // Kick off paged photo load
    setPhotos([]); setOffset(0);
    loadPhotos(0, w.id);
  }, [w]); // eslint-disable-line

  const loadPhotos = useCallback(async (nextOffset, weddingId) => {
    if (!weddingId) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/media", {
        params: { wedding_id: weddingId, media_type: "image", purpose: "gallery", limit: PAGE_SIZE, offset: nextOffset },
      });
      setTotal(data.total);
      setPhotos((prev) => nextOffset === 0 ? data.items : [...prev, ...data.items]);
      setOffset(nextOffset + data.items.length);
    } finally {
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (!w) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !loadingMore && photos.length < total) {
          loadPhotos(offset, w.id);
        }
      });
    }, { rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  }, [w, offset, photos.length, total, loadingMore, loadPhotos]);

  if (notFound) return (
    <PublicLayout>
      <div className="pt-40 text-center px-5">
        <h1 className="font-display italic text-5xl">Not found</h1>
        <Link to="/weddings" className="mt-6 inline-block btn-pill btn-outline">Back to Weddings</Link>
      </div>
    </PublicLayout>
  );

  if (!w) return (
    <PublicLayout>
      <div className="pt-40 text-center text-[color:var(--text-muted)]">Loading…</div>
    </PublicLayout>
  );

  const heroSrcSet = buildSrcSet(w.cover);
  const heroSrc = pickMediaUrl(w.cover, 2000) || w.cover_url;

  return (
    <PublicLayout>
      <section
        className="relative min-h-[80vh] md:min-h-screen w-full overflow-hidden"
        style={w.cover?.dominant_color ? { backgroundColor: w.cover.dominant_color } : undefined}
      >
        {heroSrc && (
          <img
            src={heroSrc}
            srcSet={heroSrcSet}
            sizes="100vw"
            alt={w.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/85" />
        <div className="relative z-10 pt-32 md:pt-40 pb-16 md:pb-24 px-5 md:px-10 max-w-[1500px] mx-auto min-h-[80vh] md:min-h-screen flex flex-col justify-end">
          <Link to="/weddings" data-testid="wedding-back" className="eyebrow flex items-center gap-2 mb-8 w-fit link-underline">
            <ArrowLeft size={14} /> Back
          </Link>
          <div className="eyebrow text-[color:var(--text-muted)] mb-3">{w.location}</div>
          <h1 className="font-display italic text-[14vw] md:text-[9vw] leading-[0.9] tracking-tight">{w.name}</h1>
          {w.mood_line && <p className="mt-6 text-lg md:text-2xl max-w-2xl text-[color:var(--text-muted)] italic font-display">{w.mood_line}</p>}
        </div>
      </section>

      {w.description && (
        <section className="py-16 md:py-24 border-t border-[color:var(--border)]">
          <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
            <div className="eyebrow mb-4">The Wedding</div>
            <p className="text-lg md:text-2xl text-[color:var(--text)] leading-relaxed">{w.description}</p>
          </div>
        </section>
      )}

      {photos.length > 0 && (
        <section data-testid="wedding-gallery" className="py-8 md:py-16">
          <div className="max-w-[1500px] mx-auto px-5 md:px-10">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-3">
              {photos.map((p, i) => {
                const patterns = ["md:col-span-4 aspect-[16/10]", "md:col-span-2 aspect-[4/5]", "md:col-span-2 aspect-square", "md:col-span-2 aspect-[4/5]", "md:col-span-2 aspect-square", "md:col-span-4 aspect-[16/10]"];
                const src = pickMediaUrl(p, 1200) || p.url;
                return (
                  <figure
                    key={p.id}
                    className={`hover-scale ${patterns[i % patterns.length]}`}
                    style={p.dominant_color ? { backgroundColor: p.dominant_color } : undefined}
                  >
                    <img
                      src={src}
                      srcSet={buildSrcSet(p)}
                      sizes="(max-width: 768px) 100vw, 50vw"
                      alt={p.alt_text || p.caption || w.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </figure>
                );
              })}
            </div>
            {photos.length < total && (
              <div ref={sentinelRef} data-testid="wedding-gallery-sentinel" className="text-center py-10 text-[color:var(--text-muted)] text-xs eyebrow">
                {loadingMore ? "Loading more moments…" : `${total - photos.length} more`}
              </div>
            )}
          </div>
        </section>
      )}

      {videos.length > 0 && (
        <section className="py-16 md:py-24 border-t border-[color:var(--border)]">
          <div className="max-w-[1500px] mx-auto px-5 md:px-10">
            <div className="eyebrow mb-6">Films</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {videos.map((v, i) => (
                <video key={i} src={v.url} poster={v.thumbnail_url} controls className="w-full aspect-video bg-black" playsInline preload="metadata" />
              ))}
            </div>
          </div>
        </section>
      )}

      {reels.length > 0 && (
        <section className="py-16 md:py-24 border-t border-[color:var(--border)]">
          <div className="max-w-[1500px] mx-auto px-5 md:px-10">
            <div className="eyebrow mb-6">Reels from this wedding</div>
            <div className="flex gap-4 overflow-x-auto hide-scrollbar snap-x-mandatory">
              {reels.map((r) => (
                <div key={r.id} className="snap-start flex-shrink-0 w-[72vw] md:w-[280px] aspect-[9/16] relative hover-scale">
                  <img src={r.thumbnail_url || r.video_url} alt={r.title} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute top-3 left-3 w-9 h-9 rounded-full bg-[color:var(--text)]/90 flex items-center justify-center">
                    <Play size={14} className="text-[color:var(--bg)]" fill="currentColor" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
